import { captureErrorToSentry } from "@helpers/sentry-logger";
import { isHttpUrl } from "@memorang/helpers/url";
import type { OriginalItemTypes } from "@memorang/types";
import { isPteAssessorSpeakingItem } from "../constants/pte/assessor";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import useSummativeAssessmentStore from "../hooks/useSummativeAssessmentStore";
import type { PteWorkflowUserResponse } from "../types/pte-assessor";
import { getNormalizedScore } from "./ai-feedback";
import {
	type AssessmentResultDTO,
	fetchAssessorResult,
} from "./assessor-client";
import { postAiAnswerEvent } from "./post-ai-answer-event";
import {
	buildAudioFileName,
	rawAnswerOf,
	toAudioResponse,
	toTextResponse,
} from "./pte-user-response";
import { uploadRecording } from "./recording";
import { findSessionItemById } from "./session";
import { type Sleep, defaultSleep } from "./sleep";
import { hasCapturedResponse } from "./summative-ai-gate";
import { getTimeOnItemSeconds } from "./time-on-item";
import { withRetries } from "./with-retries";

export type RunAssessmentCtx = {
	userId: string;
	projectId: number;
	organizationId: number;
	sleep?: Sleep;
};

const toSubskillAssessments = (criteria: AssessmentResultDTO["criteria"]) =>
	criteria.map((criterion) => ({
		key: criterion.id,
		name: criterion.name,
		score: criterion.score,
		...(criterion.displayScore !== undefined
			? { displayScore: criterion.displayScore }
			: {}),
		maxScore: criterion.maxScore,
		...(criterion.normalizedScore !== undefined
			? { normalizedScore: criterion.normalizedScore }
			: {}),
		...(criterion.multiplier !== undefined
			? { multiplier: criterion.multiplier }
			: {}),
	}));

type ResolveUserResponseParams = {
	userResponse: unknown;
	itemTypeId: OriginalItemTypes;
	itemId: number;
	userId: string;
	sessionId: number;
	sleep: Sleep;
};

type ResolvedUserResponse =
	| { ok: true; userResponse: PteWorkflowUserResponse }
	| { ok: false };

const resolveUserResponse = async (
	params: ResolveUserResponseParams,
): Promise<ResolvedUserResponse> => {
	const { userResponse, itemTypeId, itemId, userId, sessionId, sleep } = params;

	if (!isPteAssessorSpeakingItem(itemTypeId)) {
		const text = toTextResponse(userResponse);
		return text ? { ok: true, userResponse: text } : { ok: false };
	}

	// A resume-hydrated userResponse is already an uploaded S3 URL — don't re-upload it.
	if (isHttpUrl(userResponse)) {
		return { ok: true, userResponse: toAudioResponse(userResponse) };
	}

	const blobOrUri = userResponse as Blob | string;
	const fileName = buildAudioFileName(userId, sessionId, itemId);
	const url = await withRetries(
		() => uploadRecording(blobOrUri, fileName),
		sleep,
	);
	if (!url) {
		captureErrorToSentry(
			new Error("Summative assessment: audio upload exhausted retries"),
			{ itemId },
		);
		return { ok: false };
	}
	return { ok: true, userResponse: toAudioResponse(url) };
};

const applyAssessmentResult = (
	itemId: number,
	rawAnswer: string,
	result: AssessmentResultDTO,
) => {
	const subskillAssessments = toSubskillAssessments(result.criteria);
	const aiFeedbackStore = useAIFeedbackStore.getState();
	aiFeedbackStore.updateSubskillAssessments(itemId, subskillAssessments);
	aiFeedbackStore.updateScore(itemId, result.overall.result);
	if (result.overall.displayResult !== undefined) {
		aiFeedbackStore.updateDisplayScore(itemId, result.overall.displayResult);
	}
	aiFeedbackStore.updateExplanation(itemId, result.feedback);
	if (result.transcription) {
		aiFeedbackStore.updateTranscription(itemId, result.transcription);
	}
	aiFeedbackStore.updateAnswer(itemId, rawAnswer);
	aiFeedbackStore.updateStatus(itemId, "COMPLETE");
	return subskillAssessments;
};

export const runAssessment = async (
	itemId: number,
	ctx: RunAssessmentCtx,
): Promise<void> => {
	const userResponse = useAIFeedbackStore.getState().userResponse?.[itemId];
	if (!hasCapturedResponse(itemId)) {
		return;
	}

	const sessionState = useSessionStore.getState();
	const item = findSessionItemById(sessionState.sessionItems, itemId);
	const itemTypeId = item?.metadata?.type as OriginalItemTypes | undefined;
	if (!(item && itemTypeId)) {
		return;
	}

	// Capture time before awaits; ensures timestamp isn't cleared and covers re-grades.
	const timeTakenSeconds =
		getTimeOnItemSeconds(sessionState.itemViewedTimestamps, itemId) ??
		item.answer?.timeTaken;

	// Allocate seq before any await, so it reflects invocation order, not upload speed.
	const mySeq = useSummativeAssessmentStore.getState().beginRun(itemId);

	const sleep = ctx.sleep ?? defaultSleep;
	const resolved = await resolveUserResponse({
		userResponse,
		itemTypeId,
		itemId,
		userId: ctx.userId,
		sessionId: sessionState.id,
		sleep,
	});

	if (!useSummativeAssessmentStore.getState().isLatestRun(itemId, mySeq)) {
		return;
	}

	if (!resolved.ok) {
		useSummativeAssessmentStore.getState().markError(itemId, mySeq);
		return;
	}
	const rawAnswer = rawAnswerOf(resolved.userResponse);

	await postAiAnswerEvent(
		{
			itemId,
			mode: "PENDING",
			rawAnswer,
			projectId: ctx.projectId,
			organizationId: ctx.organizationId,
			timeTakenSeconds,
		},
		sleep,
	);

	if (!useSummativeAssessmentStore.getState().isLatestRun(itemId, mySeq)) {
		return;
	}

	const result = await withRetries(
		() =>
			fetchAssessorResult({
				itemId,
				itemTypeId,
				item,
				userResponse: resolved.userResponse,
				userId: ctx.userId,
				projectId: ctx.projectId,
				sessionId: sessionState.id,
			}),
		sleep,
	);

	if (!useSummativeAssessmentStore.getState().isLatestRun(itemId, mySeq)) {
		return;
	}

	if (!result) {
		captureErrorToSentry(
			new Error("Summative assessment: assessor call exhausted retries"),
			{ itemId },
		);
		useSummativeAssessmentStore.getState().markError(itemId, mySeq);
		return;
	}

	const subskillAssessments = applyAssessmentResult(itemId, rawAnswer, result);
	const normalizedScore = getNormalizedScore(
		result.overall.result,
		result.overall.maxPoints,
	);

	const posted = await postAiAnswerEvent(
		{
			itemId,
			mode: "COMPLETE",
			rawAnswer,
			feedback: result.feedback,
			subskillAssessments,
			transcription: result.transcription,
			score: normalizedScore,
			points: result.overall.result,
			normalizedPoints: result.overall.normalizedResult,
			displayPoints: result.overall.displayResult,
			projectId: ctx.projectId,
			organizationId: ctx.organizationId,
			timeTakenSeconds,
		},
		sleep,
	);

	// Mark assessed only after #2 persists, else the score is stranded and never re-fired.
	if (posted) {
		useSummativeAssessmentStore.getState().markAssessed(itemId, mySeq);
	} else {
		useSummativeAssessmentStore.getState().markError(itemId, mySeq);
	}
};
